import React from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, useSheetDismiss } from '../../../../components/Sheet';
import { Button } from '../../../../components/Button';
import type { BlogArticle, BlogCategory } from '../../../../mock-data/types';
import { ArticleView } from '../../../blog-preview/components/ArticleView';
import { Body, Note } from './PreviewSheet.styles';

export type PreviewSheetProps = {
  /** The draft as it stands right now, shaped as the article it would become. */
  article: BlogArticle;
  articles: BlogArticle[];
  categories: BlogCategory[];
  onClose: () => void;
};

/**
 * The article as a reader would meet it, over the editor rather than instead of
 * it: the same `ArticleView` the preview route renders, fed the draft in hand
 * instead of the saved copy, so what the author is typing is what they see.
 *
 * Full width (`maxWidth="100%"`), unlike the studio's 560px standard dialog: this
 * renders a whole article page — cover, two-column body, outro panels — and at the
 * standard width the body column collapses to a gutter and the preview stops
 * resembling the page it is previewing. The sheet takes the width the backdrop
 * gives it, which on desktop is the content card rather than the whole viewport.
 *
 * Nothing inside links anywhere. `ArticleView` renders its "Keep reading" and
 * prev/next cards inert when it has no navigation handler, which is what this
 * sheet wants: following one would leave the page and take the unsaved draft with
 * it. Reaching those articles is what the preview route is for.
 */
export const PreviewSheet = ({ article, articles, categories, onClose }: PreviewSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  return (
    <Sheet
      title={t('Preview')}
      onClose={onClose}
      closeHandle={closeHandle}
      maxWidth="100%"
      maxHeight="100%"
      footer={<Button label={t('Back to editing')} variant="outline" onClick={dismiss} />}
    >
      <Body>
        <Note text="This is your draft as it stands, including changes you have not saved yet." tag="span" />
        <ArticleView article={article} articles={articles} categories={categories} />
      </Body>
    </Sheet>
  );
};

export default PreviewSheet;
